import { useState, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const container = {
    hidden: { opacity: 1, scale: 0 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            delayChildren: 0.3,
            staggerChildren: 0.2
        }
    }
};

const item = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1
    }
};

// Utility function to chunk an array into smaller arrays of a specified size
const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
    }
    return result;
};

const ImageShowdown = ({ images }) => {
    const ref = useRef(null);

    // Split the images array into smaller arrays of size 2
    const chunkedImages = chunkArray(images, 2);
    const inView = useInView(ref, { once: true });
    const [selectedId, setSelectedId] = useState(null);

    const handleClose = () => setSelectedId(null);

    const buildLink = (clickable, index) => (
        <motion.i key={`link-${index}`} className={`absolute bottom-2 text-3xl cursor-pointer `}
            whileHover={{ scale: 1.2 }}
            transition={{ duration: 0.3 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ color: clickable.color, left: `${index * 40 + 4}px` }}
        >
            <a href={clickable.link} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={clickable.icon} />

            </a>
        </motion.i>
    );


    return (
        <>
            <motion.ul
                ref={ref}
                className="grid grid-cols-2 md:grid-cols-3 gap-10 w-2/3 mt-10"
                variants={container}
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
            >
                {chunkedImages.map((imageGroup, groupIndex) => (
                    <div key={`group-${groupIndex}`} className="grid gap-4">
                        {imageGroup.map((image, index) => (
                            <motion.li key={`img-${index}`} className="h-auto max-w-full rounded-lg border-2 border-gray-500"
                                variants={item} onClick={() => setSelectedId(image)}
                                whileHover={{ filter: "brightness(50%)" }} 
                            >
                                <img src={`${image.path}`} alt="random" className="h-auto max-w-full rounded-lg" />
                            </motion.li>
                        ))}
                    </div>
                ))}
            </motion.ul>
            <AnimatePresence>
                {selectedId && (
                    <motion.div
                        className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 backdrop-blur-sm"
                        onClick={handleClose}
                    >
                        <motion.div
                            layoutId={selectedId.path}
                            className="relative w-1/4 mt-10"
                            onClick={(e) => e.stopPropagation()} // Prevent click inside modal from closing
                        >
                            <motion.img
                                src={`${selectedId.path}`}
                                alt="random"
                                className="h-auto w-full rounded-lg border-2 border-gray-500"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                            />
                            <motion.button
                                onClick={handleClose}
                                className="absolute top-2 right-2 p-2 text-primary-100 text-xl leading-none bg-white rounded-full border-2 border-primary-500 focus:outline-none hover:bg-primary-500 hover:text-white"
                                style={{ width: "40px", height: "40px", lineHeight: "0" }}
                            >
                                &times;
                            </motion.button>

                            {selectedId.clickables && selectedId.clickables.map((clickable, index) => buildLink(clickable, index))}

                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default ImageShowdown;
